import { DateTime } from "luxon";
import { atom } from "jotai";
import { createListAtoms } from "../../common/DataList";

// ============================================================
// Liste des allocataires — configuration pure (testable sans DOM) :
// filtres par défaut, définitions des champs, construction de la requête.
// L'export CSV de cette liste vit dans le module d'exports (LISTE_ALLOCATAIRE_FLUX).
// ============================================================

export const RECIPIENT_SEARCH_ENDPOINT = "recipients/search";
export const ORIENTATION_PARTNERS_ENDPOINT = "organizations/orientation_partners";

export const RECIPIENT_DEFAULT_FILTERS = {
  terms: "",
  isActivated: "",
  origin: "",
  rdspAvailable: "",
  orientation: "",
  maritalStatus: "",
  lastOrientationAt: "",
  isLastOrientationAfterEG: false,
  professionalSituation: "",
  createdAtAfter: "",
  createdAtBefore: "",
};

export const { searchAtom: recipientSearchAtom, resultsAtom: recipientResultsAtom } =
  createListAtoms({ defaultFilters: RECIPIENT_DEFAULT_FILTERS });
export const orientationPartnersAtom = atom([]);

// `state.constants.items` vaut `[]` tant que les constantes ne sont pas chargées.
export function normalizeConstants(items) {
  return items && !Array.isArray(items) ? items : {};
}

const text = (value) => (value === undefined || value === null ? "" : value);

// Corps de la requête POST recipients/search — mêmes clés et mêmes formats que
// l'écran historique (chaînes venant du DOM, booléen pour la case à cocher).
export function buildRecipientSearchPayload(search) {
  const f = { ...RECIPIENT_DEFAULT_FILTERS, ...(search.filters || {}) };
  return {
    filters: {
      terms: text(f.terms),
      isActivated: text(f.isActivated),
      origin: text(f.origin),
      rdspAvailable: text(f.rdspAvailable),
      orientation: text(f.orientation),
      maritalStatus: text(f.maritalStatus),
      lastOrientationAt: text(f.lastOrientationAt),
      isLastOrientationAfterEG: !!f.isLastOrientationAfterEG,
      professionalSituation: text(f.professionalSituation),
      createdAtAfter: text(f.createdAtAfter),
      createdAtBefore: text(f.createdAtBefore),
    },
    page: Number(search.page) || 1,
    nbItemsByPage: Number(search.nbItemsByPage) || 25,
  };
}

export const RECIPIENT_FILTER_GROUPS = [
  { id: "compte", title: "Compte" },
  { id: "parcours", title: "Parcours" },
  { id: "situation", title: "Situation" },
  { id: "dates", title: "Dates" },
];

export const YES_NO_OPTIONS = [
  { id: 1, name: "Oui" },
  { id: 0, name: "Non" },
];

// Refonte des filtres (25/09/2026) : « Orientation » et « Compte » passent
// dans la barre de recherche (`principal`), les choix Oui / Non en options
// jointes (un clic), la date de création en une période. Mêmes clés, mêmes
// valeurs envoyées à l'API.
export function getRecipientFilterDefinitions({ constants = {}, orgs = [] } = {}) {
  return [
    {
      name: "isActivated",
      group: "compte",
      label: "Compte activé ?",
      chipLabel: "Compte",
      type: "segmented",
      principal: true,
      firstOptionLabel: "Tous",
      options: [
        { id: 1, name: "Activé" },
        { id: 0, name: "Non activé" },
      ],
    },
    {
      name: "origin",
      group: "compte",
      label: "Origine",
      type: "select",
      firstOptionLabel: "Toutes",
      options: constants.RECIPIENT_ORIGINS || [],
    },
    {
      name: "rdspAvailable",
      group: "compte",
      label: "Possède un RDO",
      chipLabel: "RDO",
      type: "segmented",
      firstOptionLabel: "Tous",
      options: YES_NO_OPTIONS,
    },
    {
      name: "orientation",
      group: "parcours",
      label: "Orientation",
      type: "select",
      principal: true,
      firstOptionLabel: "Toutes",
      dataIndex: "slugName",
      options: [...orgs, { id: "none", slugName: "none", name: "Sans orientation" }],
    },
    {
      name: "lastOrientationAt",
      group: "parcours",
      label: "Date minimum de dernière orientation",
      chipLabel: "Dernière orientation ≥",
      type: "date",
      presets: [
        {
          label: "−3 mois",
          getValue: () => DateTime.local().minus({ months: 3 }).toISO(),
        },
      ],
    },
    {
      name: "isLastOrientationAfterEG",
      group: "parcours",
      label: "Dont l'avant-dernière orientation a été décidée en EG",
      chipLabel: "Avant-dernière orientation en EG",
      type: "checkbox",
    },
    {
      name: "maritalStatus",
      group: "situation",
      label: "Situation familiale",
      type: "select",
      firstOptionLabel: "Toutes",
      options: constants.MARITAL_STATUS || [],
    },
    {
      name: "professionalSituation",
      group: "situation",
      label: "Situation professionnelle",
      type: "select",
      size: "lg",
      firstOptionLabel: "Toutes",
      options: constants.PROFESSIONAL_SITUATIONS || [],
    },
    {
      name: "createdAt",
      group: "dates",
      label: "Création",
      chipLabel: "Créé",
      type: "dateRange",
      from: "createdAtAfter",
      to: "createdAtBefore",
      fromLabel: "Créé à partir du",
      toLabel: "Créé jusqu'au (inclus)",
    },
  ];
}
